import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPost } from "../../lib/adminApi";
import { useUiPreference } from "../../lib/uiPreferences";
import { ErrorBanner, OvergeslagenBanner, PaginationBar } from "../../components/admin/AdminUI";
import ViewModeToggle from "../../components/admin/ViewModeToggle";
import { Button, Card, Input, Label, MeasureRule, PageHeader, StatusBadge } from "../../components/fulkra";
import { TH, TD, RIJ } from "../../components/fulkra/tabel";

/**
 * Werkgevers in de huisstijl.
 *
 * Restyling, geen herbouw: dezelfde bevraging, dezelfde zoekfilter, dezelfde twee
 * weergaven (blokjes en rijen), hetzelfde modaal met dezelfde validatie, en alle
 * data-testid's ongewijzigd.
 */

export default function WerkgeversPage() {
  const [offset, setOffset] = useState(0);
  const [zoek, setZoek] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useUiPreference("werkgevers_view", "blokjes");
  const LIMIT = 24;
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["werkgevers", { offset }],
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: LIMIT, offset }),
    keepPreviousData: true,
  });

  const createMut = useMutation({
    mutationFn: (body) => adminPost("/api/admin/werkgevers", body),
    onSuccess: () => { toast.success("Werkgever aangemaakt"); setCreateOpen(false); qc.invalidateQueries({ queryKey: ["werkgevers"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Aanmaken mislukt"),
  });

  const items = q.data?.items || q.data || [];
  const total = q.data?.total ?? items.length;

  const gefilterd = useMemo(() => {
    if (!zoek.trim()) return items;
    const z = zoek.trim().toLowerCase();
    return items.filter((w) =>
      (w.bedrijfsnaam || "").toLowerCase().includes(z) ||
      (w.plaats || "").toLowerCase().includes(z) ||
      (w.aanmeldcode || "").toLowerCase().includes(z)
    );
  }, [items, zoek]);

  return (
    <div data-testid="werkgevers-page">
      <PageHeader
        title="Werkgevers"
        subtitle={`${total} bedrijven in totaal`}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-4)" }}>
            <ViewModeToggle value={view} onChange={setView} testid="werkgevers-viewmode" />
            <Button size="sm" style={{ minHeight: 34 }} onClick={() => setCreateOpen(true)} data-testid="btn-werkgever-nieuw">
              Nieuwe werkgever
            </Button>
          </div>
        }
      />
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="werkgever" meervoud="werkgevers"
        testid="werkgevers-overgeslagen"
      />

      <div style={{ marginBottom: "var(--ruimte-6)", width: 420, maxWidth: "100%" }}>
        <Input
          mobiel={false}
          type="text"
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek op bedrijfsnaam, plaats of aanmeldcode"
          data-testid="werkgevers-zoek"
          aria-label="Zoek werkgevers"
          style={{ height: "var(--veld-hoogte-desktop)", minHeight: "var(--veld-hoogte-desktop)" }}
        />
      </div>

      {q.isLoading ? (
        <Leeg>Laden…</Leeg>
      ) : gefilterd.length === 0 ? (
        <Leeg testid="werkgevers-empty">
          {zoek ? "Geen werkgevers gevonden voor deze zoekopdracht." : "Nog geen werkgevers."}
        </Leeg>
      ) : view === "rijen" ? (
        <WerkgeversTabel items={gefilterd} />
      ) : (
        <div
          data-testid="werkgevers-grid"
          style={{ display: "grid", gap: "var(--ruimte-6)", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}
        >
          {gefilterd.map((w) => (
            <WerkgeverTegel key={w.werkgever_id} w={w} />
          ))}
        </div>
      )}

      <PaginationBar
        offset={offset} limit={LIMIT} total={total}
        onPrev={() => setOffset(Math.max(0, offset - LIMIT))}
        onNext={() => setOffset(offset + LIMIT)}
      />
      {createOpen ? (
        <NieuweWerkgeverModal
          onClose={() => setCreateOpen(false)}
          onSubmit={(body) => createMut.mutate(body)}
          loading={createMut.isPending}
        />
      ) : null}
    </div>
  );
}

function Leeg({ children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
        padding: "var(--ruimte-10)", textAlign: "center",
        fontSize: "var(--fs-body)", color: "var(--text-soft)",
      }}
    >
      {children}
    </div>
  );
}




function WerkgeverTegel({ w }) {
  return (
    <div
      data-testid={`werkgever-tegel-${w.werkgever_id}`}
      style={{
        display: "flex", flexDirection: "column", gap: "var(--ruimte-5)",
        background: "var(--surface-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
        padding: "var(--pad-kaart)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--ruimte-4)" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3
            data-testid={`werkgever-naam-${w.werkgever_id}`}
            style={{ font: "var(--text-h3)", letterSpacing: 0, color: "var(--text-strong)" }}
          >
            {w.bedrijfsnaam}
          </h3>
          <p style={{ marginTop: 2, fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
            {[w.sector, w.plaats].filter(Boolean).join(" · ") || "Geen sector / plaats ingevuld"}
          </p>
        </div>
        <StatusBadge value={w.status} data-testid={`werkgever-status-${w.werkgever_id}`} />
      </div>

      {w.contactpersoon_naam ? (
        <div
          style={{
            background: "var(--surface-sunken)", borderRadius: "var(--radius)",
            padding: "var(--ruimte-5)", fontSize: "var(--fs-sm)",
          }}
        >
          <div style={{ fontWeight: "var(--fw-medium)", color: "var(--text-body)" }}>{w.contactpersoon_naam}</div>
          <div style={{ color: "var(--text-soft)" }}>{w.contactpersoon_rol || "Contactpersoon"}</div>
        </div>
      ) : (
        <div
          style={{
            border: "1px dashed var(--lijn-sterk)", borderRadius: "var(--radius)",
            padding: "var(--ruimte-5)", fontSize: "var(--fs-sm)", color: "var(--text-soft)",
          }}
        >
          Contactpersoon nog niet ingevuld
        </div>
      )}

      <MeasureRule height={9} spacing={7} style={{ marginTop: "var(--ruimte-2)" }} />

      <dl style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--ruimte-5)", margin: 0 }}>
        <div>
          <dt style={{ fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "var(--text-soft)" }}>
            Deal-aantal
          </dt>
          <dd style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-body)", fontVariantNumeric: "tabular-nums" }}>
            {w.aantal_werknemers_deal ?? "—"}
          </dd>
        </div>
        <div>
          <dt style={{ fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "var(--text-soft)" }}>
            Aanmeldcode
          </dt>
          <dd style={{ margin: 0, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)", color: "var(--text-body)" }}>
            {w.aanmeldcode || "—"}
          </dd>
        </div>
      </dl>

      <div
        style={{
          marginTop: "var(--ruimte-6)", borderTop: "1px solid var(--border-subtle)",
          padding: "var(--ruimte-5) var(--pad-kaart)",
          margin: "var(--ruimte-4) calc(var(--pad-kaart) * -1) calc(var(--pad-kaart) * -1)",
        }}
      >
        <Link
          to={`/admin/werkgevers/${w.werkgever_id}`}
          data-testid={`btn-werkgever-detail-${w.werkgever_id}`}
          style={{ fontSize: "var(--fs-body)", fontWeight: "var(--fw-medium)", color: "var(--text-action)" }}
        >
          Beheren
        </Link>
      </div>
    </div>
  );
}

function WerkgeversTabel({ items }) {
  return (
    <div
      data-testid="werkgevers-tabel"
      style={{
        overflowX: "auto", background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-body)" }}>
        <thead style={{ background: "var(--surface-sunken)" }}>
          <tr>
            {["Bedrijfsnaam", "Plaats", "Contactpersoon", "Deal-aantal", "Aanmeldcode", "Status"].map((k) => (
              <th
                key={k}
                scope="col"
                style={k === "Deal-aantal" ? { ...TH, textAlign: "right" } : TH}
              >
                {k}
              </th>
            ))}
            <th scope="col" style={{ ...TH, textAlign: "right" }}>Actie</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.werkgever_id} data-testid={`werkgever-row-${w.werkgever_id}`} style={RIJ}>
              <td style={TD}>{w.bedrijfsnaam}</td>
              <td style={TD}>{w.plaats || "—"}</td>
              <td style={TD}>{w.contactpersoon_naam || "—"}</td>
              <td style={{ ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{w.aantal_werknemers_deal ?? "—"}</td>
              <td style={{ ...TD, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)" }}>{w.aanmeldcode || "—"}</td>
              <td style={TD}><StatusBadge value={w.status} data-testid={`werkgever-status-${w.werkgever_id}`} /></td>
              <td style={{ ...TD, textAlign: "right" }}>
                <Link
                  to={`/admin/werkgevers/${w.werkgever_id}`}
                  data-testid={`btn-werkgever-detail-${w.werkgever_id}`}
                  style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-medium)", color: "var(--text-action)" }}
                >
                  Beheren →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Eén veld in het werkgever-formulier.
 *
 * Bewust op modulniveau en niet binnen het venster-component: een component dat bij elke
 * render opnieuw wordt gedefinieerd, is voor React elke keer een ander type. Hij wordt dan
 * per toetsaanslag opnieuw opgebouwd en de cursor springt uit het veld.
 */
function WerkgeverVeld({ naam, label, verplicht, type, placeholder, breed, waarde, onChange, fout }) {
  return (
    <div style={{ display: "grid", gap: "var(--ruimte-3)", gridColumn: breed ? "1 / -1" : "auto" }}>
      <Label htmlFor={`wg-${naam}`} required={verplicht}>{label}</Label>
      <Input
        id={`wg-${naam}`}
        type={type}
        value={waarde}
        onChange={(e) => onChange(naam, e.target.value)}
        data-testid={`modal-werkgever-${naam}`}
        placeholder={placeholder}
        aria-invalid={fout ? true : undefined}
      />
      {fout ? (
        <span data-testid={`fout-${naam}`} style={{ fontSize: "var(--fs-xs)", color: "var(--text-alarm)" }}>
          {fout}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Alle gegevens van een werkgever staan meteen in het aanmaakformulier.
 *
 * Tot 2026-08-20 vroeg dit venster alleen de bedrijfsnaam; al het andere — sector, adres,
 * postcode, plaats, aanmeetlocatie, aantal werknemers en de contactpersoon — moest daarna
 * via "Bewerken" op het detailscherm worden bijgevuld. Dat gebeurde in de praktijk niet,
 * waardoor er werkgevers in het systeem stonden zonder een adres om mail heen te sturen.
 *
 * Vier velden zijn verplicht: de bedrijfsnaam, en naam, e-mail en telefoon van de persoon
 * met wie we contact hebben. Zonder die drie is een werkgever niet te benaderen, en dat is
 * precies waar het traject op stukloopt. De rest mag later.
 */
function NieuweWerkgeverModal({ onClose, onSubmit, loading }) {
  const [form, setForm] = useState({
    bedrijfsnaam: "", kvk_nummer: "", sector: "",
    adres: "", postcode: "", plaats: "", standaard_aanmeet_locatie: "",
    aantal_werknemers_deal: "",
    contactpersoon_naam: "", contactpersoon_rol: "",
    contactpersoon_email: "", contactpersoon_telefoon: "",
  });
  const [getoond, setGetoond] = useState(false);   // pas na een poging fouten tonen

  function set(veld, waarde) {
    setForm((f) => ({ ...f, [veld]: waarde }));
  }

  // De meldingen staan hier bij elkaar, zodat het scherm dezelfde eisen stelt als de
  // server. Anders krijgt de beheerder pas ná Aanmaken een Engelse validatiefout terug.
  const fouten = {};
  if (form.bedrijfsnaam.trim().length < 2) fouten.bedrijfsnaam = "Vul de bedrijfsnaam in.";
  if (!form.contactpersoon_naam.trim()) fouten.contactpersoon_naam = "Vul de naam van de contactpersoon in.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactpersoon_email.trim())) {
    fouten.contactpersoon_email = "Vul een geldig e-mailadres in.";
  }
  if (form.contactpersoon_telefoon.trim().length < 8) fouten.contactpersoon_telefoon = "Vul het telefoonnummer in.";
  if (form.kvk_nummer.trim() && !/^\d{8}$/.test(form.kvk_nummer.trim())) {
    fouten.kvk_nummer = "Een KVK-nummer bestaat uit acht cijfers.";
  }
  if (form.postcode.trim() && !/^\d{4}\s?[A-Za-z]{2}$/.test(form.postcode.trim())) {
    fouten.postcode = "Een postcode ziet eruit als 1234 AB.";
  }
  const compleet = Object.keys(fouten).length === 0;

  function versturen() {
    setGetoond(true);
    if (!compleet) return;
    // Lege velden als null wegschrijven, niet als lege tekst: dan staat er in de database
    // "niet ingevuld" in plaats van "ingevuld met niets".
    const body = {};
    Object.entries(form).forEach(([veld, waarde]) => {
      const schoon = typeof waarde === "string" ? waarde.trim() : waarde;
      body[veld] = schoon === "" ? null : schoon;
    });
    if (body.aantal_werknemers_deal != null) {
      const n = parseInt(body.aantal_werknemers_deal, 10);
      body.aantal_werknemers_deal = Number.isFinite(n) ? n : null;
    }
    onSubmit(body);
  }

  const RASTER = { display: "grid", gap: "var(--ruimte-5)", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" };
  const KOPJE = {
    font: "var(--text-label)", color: "var(--text-soft)", textTransform: "uppercase",
    letterSpacing: "0.06em", fontSize: "var(--fs-xs)", marginBottom: "var(--ruimte-4)",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Nieuwe werkgever"
      data-testid="modal-werkgever-nieuw"
      className="fulkra"
      style={{
        position: "fixed", inset: 0, zIndex: 40, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "var(--ruimte-7)", background: "var(--overlay)",
        overflowY: "auto",
      }}
    >
      <div style={{ width: "100%", maxWidth: 720, margin: "auto" }}>
        <Card title="Nieuwe werkgever">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)", marginBottom: "var(--ruimte-6)" }}>
            Vul zoveel mogelijk meteen in. Bedrijfsnaam en de gegevens van de contactpersoon
            zijn verplicht; de rest kunt u later aanvullen.
          </p>

          <div style={{ display: "grid", gap: "var(--ruimte-7)" }}>
            <section>
              <div style={KOPJE}>Bedrijf</div>
              <div style={RASTER}>
                <WerkgeverVeld naam="bedrijfsnaam" label="Bedrijfsnaam" verplicht placeholder="Zorggroep Meander" waarde={form.bedrijfsnaam} onChange={set} fout={getoond ? fouten.bedrijfsnaam : null} />
                <WerkgeverVeld naam="kvk_nummer" label="KVK-nummer" placeholder="12345678" waarde={form.kvk_nummer} onChange={set} fout={getoond ? fouten.kvk_nummer : null} />
                <WerkgeverVeld naam="sector" label="Sector" placeholder="zorg, bouw, logistiek…" waarde={form.sector} onChange={set} fout={getoond ? fouten.sector : null} />
                <WerkgeverVeld naam="aantal_werknemers_deal" label="Aantal werknemers" type="number" placeholder="30" waarde={form.aantal_werknemers_deal} onChange={set} fout={getoond ? fouten.aantal_werknemers_deal : null} />
                <WerkgeverVeld naam="adres" label="Adres" placeholder="Dorpsstraat 1" waarde={form.adres} onChange={set} fout={getoond ? fouten.adres : null} />
                <WerkgeverVeld naam="postcode" label="Postcode" placeholder="1234 AB" waarde={form.postcode} onChange={set} fout={getoond ? fouten.postcode : null} />
                <WerkgeverVeld naam="plaats" label="Plaats" placeholder="Amersfoort" waarde={form.plaats} onChange={set} fout={getoond ? fouten.plaats : null} />
                <WerkgeverVeld naam="standaard_aanmeet_locatie" label="Vaste aanmeetlocatie" placeholder="Kantine, begane grond" breed waarde={form.standaard_aanmeet_locatie} onChange={set} fout={getoond ? fouten.standaard_aanmeet_locatie : null} />
              </div>
            </section>

            <section>
              <div style={KOPJE}>Contactpersoon</div>
              <div style={RASTER}>
                <WerkgeverVeld naam="contactpersoon_naam" label="Naam" verplicht placeholder="Anne de Vries" waarde={form.contactpersoon_naam} onChange={set} fout={getoond ? fouten.contactpersoon_naam : null} />
                <WerkgeverVeld naam="contactpersoon_rol" label="Rol" placeholder="HR-manager, eigenaar…" waarde={form.contactpersoon_rol} onChange={set} fout={getoond ? fouten.contactpersoon_rol : null} />
                <WerkgeverVeld naam="contactpersoon_email" label="E-mailadres" verplicht type="email" placeholder="anne@zorggroepmeander.nl" waarde={form.contactpersoon_email} onChange={set} fout={getoond ? fouten.contactpersoon_email : null} />
                <WerkgeverVeld naam="contactpersoon_telefoon" label="Telefoonnummer" verplicht placeholder="06 12 34 56 78" waarde={form.contactpersoon_telefoon} onChange={set} fout={getoond ? fouten.contactpersoon_telefoon : null} />
              </div>
            </section>
          </div>

          {getoond && !compleet ? (
            <p role="alert" data-testid="modal-werkgever-onvolledig"
              style={{ marginTop: "var(--ruimte-6)", fontSize: "var(--fs-sm)", color: "var(--text-alarm)" }}>
              Er ontbreekt nog iets. De velden met een melding eronder moeten worden ingevuld.
            </p>
          ) : null}

          <div style={{ marginTop: "var(--ruimte-7)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
            <Button variant="secundair" onClick={onClose} data-testid="modal-werkgever-cancel">Annuleren</Button>
            <Button
              disabled={loading}
              onClick={versturen}
              data-testid="modal-werkgever-submit"
            >
              {loading ? "Bezig…" : "Aanmaken"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
